import { offerBillingLabel } from '../../lib/offerBilling';
import { formatMinorCurrency } from '@/features/commerce-admin/currency';

export interface OrderLineItemIdentifier {
  label: string;
  value: string;
}

export interface OrderLineItemViewModel {
  key: string;
  title: string;
  productName: string | null;
  offerName: string | null;
  description: string | null;
  quantity: number;
  currency: string;
  unitPriceMinor: number | null;
  originalUnitPriceMinor: number | null;
  lineTotalMinor: number | null;
  startupFeeMinor: number | null;
  billingLabel: string | null;
  serviceClass: string | null;
  planLabel: string | null;
  termLabel: string | null;
  feeCount: number;
  discountCount: number;
  includedAtNoCost: boolean;
  identifiers: OrderLineItemIdentifier[];
}

export interface OrderPricingSummaryItem {
  label: string;
  amountMinor: number;
  currency: string;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readText(record: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

function readInteger(record: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isSafeInteger(value)) return value;
    if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) {
      const parsed = Number(value);
      if (Number.isSafeInteger(parsed)) return parsed;
    }
  }
  return null;
}

function countArray(record: Record<string, unknown>, key: string): number {
  return Array.isArray(record[key]) ? record[key].length : 0;
}

function bundlePriceMode(record: Record<string, unknown>): string | null {
  const metadata = asRecord(record.metadata);
  const bundle = metadata ? asRecord(metadata.bundle) : null;
  return bundle ? readText(bundle, 'price_mode') : null;
}

function isLegacyIncludedBundleDiscount(value: unknown): boolean {
  const discount = asRecord(value);
  const metadata = discount ? asRecord(discount.metadata) : null;
  const bundle = metadata ? asRecord(metadata.bundle) : null;
  return metadata?.source === 'bundle_component' && readText(bundle || {}, 'price_mode') === 'included_free';
}

function presentedDiscountCount(record: Record<string, unknown>): number {
  const discounts = record.discounts;
  return Array.isArray(discounts)
    ? discounts.filter(discount => !isLegacyIncludedBundleDiscount(discount)).length
    : 0;
}

function normalizeCurrency(value: string | null, fallback = 'USD'): string {
  const candidate = value?.toUpperCase() || fallback.toUpperCase();
  return /^[A-Z]{3}$/.test(candidate) ? candidate : 'USD';
}

function humanize(value: string | null): string | null {
  if (!value) return null;
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());
}

function billingLabel(record: Record<string, unknown>): string | null {
  const interval = offerBillingLabel(typeof record.billing_period_months === 'number' ? record.billing_period_months : null);
  if (interval) {
    const normalized = interval.toLowerCase();
    if (normalized === 'month' || normalized === 'monthly') return 'Monthly';
    if (normalized === 'year' || normalized === 'yearly' || normalized === 'annual') return 'Annual';
    if (normalized === 'one_time' || normalized === 'once') return 'One time';
    return humanize(interval);
  }
  return humanize(readText(record, 'charge_type', 'timing'));
}

function buildIdentifiers(record: Record<string, unknown>): OrderLineItemIdentifier[] {
  const candidates: Array<[string, string | null]> = [
    ['Product', readText(record, 'product_key', 'product_id')],
    ['Variant', readText(record, 'variant_key', 'sku', 'variant_id')],
    ['Offer', readText(record, 'offer_key', 'offer_id')],
  ];
  const seen = new Set<string>();
  return candidates.flatMap(([label, value]) => {
    if (!value || seen.has(value)) return [];
    seen.add(value);
    return [{ label, value }];
  });
}

export function normalizeOrderLineItems(value: unknown, fallbackCurrency = 'USD'): OrderLineItemViewModel[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry, index) => {
    const record = asRecord(entry);
    if (!record) return [];

    const productName = readText(record, 'product_name');
    const title =
      readText(record, 'display_name', 'variant_display_name', 'variant_name', 'product_name', 'name', 'product_key') ||
      `Order item ${index + 1}`;
    const quantity = Math.max(1, readInteger(record, 'quantity') ?? 1);
    const currency = normalizeCurrency(readText(record, 'currency'), fallbackCurrency);
    const originalUnitPriceMinor = readInteger(record, 'price', 'unit_price_minor', 'amount_minor');
    const discountedUnitPriceMinor = readInteger(record, 'discounted_price', 'discounted_price_minor');
    const includedAtNoCost = bundlePriceMode(record) === 'included_free';
    const unitPriceMinor = includedAtNoCost ? 0 : discountedUnitPriceMinor ?? originalUnitPriceMinor;
    const computedLineTotal = unitPriceMinor == null ? null : unitPriceMinor * quantity;
    const lineTotalMinor = computedLineTotal != null && Number.isSafeInteger(computedLineTotal) ? computedLineTotal : null;
    const termMonths = readInteger(record, 'selected_term_months');
    const planLabel = readText(record, 'plan_code', 'unique_plan_identifier');

    return [{
      key: `${readText(record, 'offer_id', 'variant_id', 'product_id') || 'order-item'}-${index + 1}`,
      title,
      productName: productName && productName !== title ? productName : null,
      offerName: readText(record, 'offer_name', 'offer_display_name'),
      description: readText(record, 'description'),
      quantity,
      currency,
      unitPriceMinor,
      originalUnitPriceMinor:
        !includedAtNoCost && originalUnitPriceMinor != null && originalUnitPriceMinor !== unitPriceMinor
          ? originalUnitPriceMinor
          : null,
      lineTotalMinor,
      startupFeeMinor: readInteger(record, 'startup_fee', 'startup_fee_minor'),
      billingLabel: billingLabel(record),
      serviceClass: humanize(readText(record, 'service_class')),
      planLabel,
      termLabel: termMonths != null && termMonths > 0 ? `${termMonths}-month term` : null,
      feeCount: countArray(record, 'fees'),
      discountCount: presentedDiscountCount(record),
      includedAtNoCost,
      identifiers: buildIdentifiers(record),
    }];
  });
}

export function buildOrderPricingSummary(
  pricingSnapshot: unknown,
  lineItems: OrderLineItemViewModel[],
): OrderPricingSummaryItem[] {
  const pricing = asRecord(pricingSnapshot);
  const fallbackCurrency = lineItems[0]?.currency || 'USD';
  const currency = normalizeCurrency(pricing ? readText(pricing, 'currency') : null, fallbackCurrency);
  const summary: OrderPricingSummaryItem[] = [];
  const add = (label: string, amountMinor: number | null) => {
    if (amountMinor != null) summary.push({ label, amountMinor, currency });
  };

  if (pricing) {
    add('Items subtotal', readInteger(pricing, 'discounted_subtotal', 'subtotal'));
    add('Recurring total', readInteger(pricing, 'recurring_total', 'monthly_recurring_total'));
    add('Due today', readInteger(pricing, 'due_today_total', 'total_due_today', 'startup_total'));
    add('Order total', readInteger(pricing, 'grand_total'));
  }

  if (summary.length === 0 && lineItems.length > 0) {
    const currencies = new Set(lineItems.map(item => item.currency));
    const totals = lineItems.map(item => item.lineTotalMinor);
    if (currencies.size === 1 && totals.every((amount): amount is number => amount != null)) {
      add('Items subtotal', totals.reduce((total, amount) => total + amount, 0));
    }
  }
  return summary;
}

export function formatOrderCurrency(amountMinor: number | null, currency: string): string {
  if (amountMinor == null) return '—';
  return formatMinorCurrency(amountMinor, normalizeCurrency(currency));
}
